import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Loader2, ExternalLink, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import ConsentPrompt from '@/components/ConsentPrompt';

const STATUS_STYLE = {
  granted: 'bg-emerald-50 text-emerald-700',
  withdrawn: 'bg-gray-100 text-gray-600',
  not_given: 'bg-amber-50 text-amber-700',
};
const STATUS_LABEL = { granted: 'Active', withdrawn: 'Withdrawn', not_given: 'Not given' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export default function ParentPrivacyPermissions() {
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState('');
  const [data, setData] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [prompt, setPrompt] = useState(null);

  useEffect(() => {
    api.get('/safeguarding/my-children').then(r => {
      setChildren(r.data);
      if (r.data[0]) setChildId(r.data[0].id);
    });
  }, []);

  const load = useCallback(async () => {
    if (!childId) return;
    const res = await api.get('/parent/permissions', { params: { child_id: childId } });
    setData(res.data);
  }, [childId]);

  useEffect(() => { setData(null); load(); }, [load]);

  const doWithdraw = async () => {
    setBusy(true);
    try {
      const res = await api.post('/parent/permissions/withdraw', {
        document_id: withdrawing.document.id, child_id: childId, reason,
        booking_id: withdrawing.reference.booking_id || '',
        program_id: withdrawing.reference.program_id || '',
      });
      const f = res.data.flagged_for_review || {};
      toast.success(`Permission withdrawn. ${f.bookings || 0} experience(s) and ${f.internship_applications || 0} application(s) sent to your coordinator for review.`);
      setWithdrawing(null);
      setReason('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not withdraw the permission');
    }
    setBusy(false);
  };

  const grantAgain = async (item) => {
    const doc = (await api.get(`/consent/documents/${item.document.id}`)).data;
    setPrompt({ doc, item });
  };

  const acceptPrompt = async () => {
    setBusy(true);
    try {
      await api.post('/parent/permissions/accept', {
        document_id: prompt.doc.id, child_id: childId,
        booking_id: prompt.item.reference.booking_id || '',
        program_id: prompt.item.reference.program_id || '',
      });
      toast.success('Permission recorded');
      setPrompt(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not record the permission');
    }
    setBusy(false);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="privacy-permissions-page">
      <h1 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit'] tracking-tight">Privacy &amp; Permissions</h1>
      <p className="text-sm text-[#6B7280] mt-1.5 max-w-xl leading-relaxed">
        See what you have agreed to for each child, what information is shared and with whom, and
        withdraw a permission whenever you choose.
      </p>

      {children.length > 1 && (
        <div className="mt-5 max-w-xs">
          <Label>Child</Label>
          <Select value={childId} onValueChange={setChildId}>
            <SelectTrigger className="mt-1 rounded-xl h-11 text-base sm:text-sm border-gray-200" data-testid="permissions-child-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {children.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-[#9CA3AF]" /></div>
      ) : (
        <div className="mt-6 space-y-6">
          <div className="space-y-2" data-testid="permissions-list">
            {data.items.map((item, i) => (
              <div key={`${item.document.id}-${i}`} className="bg-white rounded-2xl border border-[#EFF0F2] p-4"
                   data-testid={`permission-${item.document.id}-${i}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-[#1F2937]">{item.document.title}</span>
                      <Badge className={`border-0 text-[10px] ${item.needs_renewal ? 'bg-amber-50 text-amber-700' : STATUS_STYLE[item.status]}`} data-testid={`permission-status-${item.document.id}-${i}`}>
                        {item.needs_renewal ? 'Renewal needed' : STATUS_LABEL[item.status]}
                      </Badge>
                    </div>
                    <p className="text-xs text-[#6B7280] mt-1 break-words">
                      {item.scope_label}
                      {item.accepted_at ? ` · accepted ${fmt(item.accepted_at)}` : ''}
                      {item.accepted_version ? ` · version ${item.accepted_version}` : ''}
                      {item.status === 'withdrawn' && item.withdrawn_at
                        ? ` · withdrawn ${fmt(item.withdrawn_at)}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" onClick={() => setViewDoc({ ...item.document, accepted_version: item.accepted_version })}
                            className="rounded-xl h-11 sm:h-8 text-xs border-gray-200 text-[#4B5563] font-medium flex-1 sm:flex-none"
                            data-testid={`view-document-${item.document.id}-${i}`}>
                      View text
                    </Button>
                    {item.withdrawable && (
                      <Button variant="outline" onClick={() => { setReason(''); setWithdrawing(item); }}
                              className="rounded-xl h-11 sm:h-8 text-xs border-gray-200 text-[#4B5563] font-medium hover:text-[#1F2937] flex-1 sm:flex-none"
                              data-testid={`withdraw-${item.document.id}-${i}`}>
                        Withdraw
                      </Button>
                    )}
                    {(item.status !== 'granted' || item.needs_renewal) && item.document.id !== 'workplace_visit' && (
                      <Button variant="outline" onClick={() => grantAgain(item)}
                              className="rounded-xl h-11 sm:h-8 text-xs border-gray-200 text-[#1F2937] font-semibold flex-1 sm:flex-none"
                              data-testid={`grant-${item.document.id}-${i}`}>
                        {item.status === 'withdrawn' ? 'Give again' : 'Give permission'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-[#EFF0F2] p-5" data-testid="data-sharing">
            <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-2">
              What is collected and who can see it
            </p>
            <p className="text-sm text-[#4B5563] leading-relaxed">{data.data_sharing}</p>
            <div className="flex gap-3 mt-3 flex-wrap">
              <Link to="/privacy" target="_blank" className="text-xs text-[#6B7280] hover:text-[#1F2937] font-medium inline-flex items-center gap-1 underline underline-offset-2">
                Privacy Policy <ExternalLink className="h-3 w-3" />
              </Link>
              <Link to="/student-safety" target="_blank" className="text-xs text-[#6B7280] hover:text-[#1F2937] font-medium inline-flex items-center gap-1 underline underline-offset-2">
                Student Safety Guidelines <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          </div>

          <div className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 flex gap-3" data-testid="privacy-request-route">
            <Info className="h-4 w-4 text-[#9CA3AF] shrink-0 mt-0.5" />
            <p className="text-xs text-[#4B5563] leading-relaxed">{data.privacy_request_route}</p>
          </div>

          <div className="bg-white rounded-2xl border border-[#EFF0F2] p-5" data-testid="consent-history">
            <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-3">History</p>
            {data.history.length === 0 ? (
              <p className="text-sm text-[#9CA3AF]">Nothing recorded yet.</p>
            ) : (
              <div className="space-y-1.5">
                {data.history.map((h, i) => (
                  <div key={i} className="flex flex-col sm:flex-row sm:gap-2 text-xs" data-testid={`history-row-${i}`}>
                    <span className="text-[#9CA3AF] sm:w-32 shrink-0">{fmtTime(h.at)}</span>
                    <span className="text-[#1F2937] break-words">
                      {h.document_title} — {h.action === 'granted' ? 'permission given' : 'permission withdrawn'}
                      {h.reason ? ` (${h.reason})` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* The document text */}
      <Dialog open={!!viewDoc} onOpenChange={(o) => !o && setViewDoc(null)}>
        <DialogContent className="rounded-2xl max-w-lg w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto" data-testid="document-dialog">
          {viewDoc && (
            <>
              <DialogHeader>
                <DialogTitle className="font-['Outfit']">{viewDoc.title}</DialogTitle>
                <DialogDescription>Version {viewDoc.version}</DialogDescription>
              </DialogHeader>
              <div className="space-y-2.5">
                {viewDoc.accepted_version && viewDoc.accepted_version !== viewDoc.version && (
                  <p className="text-xs text-amber-700 bg-amber-50 rounded-xl p-2.5" data-testid="document-version-note">
                    This is the current text. You accepted version {viewDoc.accepted_version}, which is
                    still valid — we only ask you again when something material changes.
                  </p>
                )}
                {(viewDoc.sections || []).map(s => (
                  <div key={s.heading}>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF]">{s.heading}</p>
                    <p className="text-sm text-[#4B5563] mt-0.5 leading-relaxed">{s.body}</p>
                  </div>
                ))}
                {(viewDoc.points || []).map(p => <p key={p} className="text-sm text-[#4B5563]">• {p}</p>)}
                <p className="text-sm text-[#1F2937] bg-[#F9FAFB] rounded-xl p-3 leading-relaxed">{viewDoc.statement}</p>
                {viewDoc.link && (
                  <Link to={viewDoc.link} target="_blank" className="text-xs text-[#6B7280] hover:text-[#1F2937] font-medium inline-flex items-center gap-1 underline underline-offset-2">
                    Read the full policy <ExternalLink className="h-3 w-3" />
                  </Link>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Withdraw */}
      <Dialog open={!!withdrawing} onOpenChange={(o) => !o && setWithdrawing(null)}>
        <DialogContent className="rounded-2xl w-[calc(100%-2rem)]" data-testid="withdraw-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[#F97D6A]" /> Withdraw {withdrawing?.document.title}?
            </DialogTitle>
            <DialogDescription>
              Your child cannot start new activities that need this permission. Anything already
              arranged is sent to your Career Coordinator to review — nothing is cancelled
              automatically, and school and safety records already created are kept. You can give the
              permission again at any time.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Reason (optional)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                      placeholder="Helps your coordinator respond"
                      className="mt-1 rounded-xl text-base sm:text-sm border-gray-200" data-testid="withdraw-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawing(null)} className="rounded-xl h-11 sm:h-10">Keep permission</Button>
            <Button onClick={doWithdraw} disabled={busy}
                    className="rounded-xl h-11 sm:h-10 bg-[#1F2937] hover:bg-[#111827] text-white"
                    data-testid="confirm-withdraw-btn">
              {busy ? 'Withdrawing…' : 'Withdraw permission'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConsentPrompt doc={prompt?.doc} open={!!prompt} onOpenChange={() => setPrompt(null)}
                     onAccept={acceptPrompt} busy={busy} />
    </div>
  );
}
